// ABOUTME: Full news history view for the warehouse/relation notifications page.
// ABOUTME: Articles grouped under month/year headers (most recent first); click opens MViewNewsArticle.

import { Box, ButtonBase, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { tokens } from "../../../../theme";
import Subheading from "../../../../components/theme/text/Subheading";
import Text from "../../../../components/theme/text/Text";
import MViewNewsArticle from "../../../../components/global/Modals/MCreateNewsArticle/MViewNewsArticle";
import { useGetNewsArchiveQuery } from "../../../../newapi/global/newsRootSlice";

const formatDate = (raw, locale) => {
    if (!raw) return "";
    const d = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(locale || "nl-NL", { day: "numeric", month: "long", year: "numeric" });
};

const groupByYearMonth = (items) => {
    const groups = new Map();
    for (const a of items) {
        const raw = a.publishedAt || a.createdAt;
        const d = raw ? new Date(raw) : null;
        const valid = d && !Number.isNaN(d.getTime());
        const key = valid ? `${d.getFullYear()}-${d.getMonth()}` : "unknown";
        if (!groups.has(key)) {
            groups.set(key, {
                year: valid ? d.getFullYear() : null,
                month: valid ? d.getMonth() : null,
                items: [],
            });
        }
        groups.get(key).items.push(a);
    }
    return Array.from(groups.values());
};

const formatGroupHeader = (year, month, locale, t) => {
    if (year === null) return t("date_unknown") || "Onbekende datum";
    const d = new Date(year, month, 1);
    const label = d.toLocaleDateString(locale, { month: "long", year: "numeric" });
    return label.charAt(0).toUpperCase() + label.slice(1);
};

const News = ({ settings = [], ...props }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [viewArticle, setViewArticle] = useState(null);
    const { data: archive } = useGetNewsArchiveQuery({ page: 1, size: 50 });
    const items = archive?.items || [];

    useEffect(() => {
        props.replaceView({ ...props.config });
        return () => { };
    }, []);

    const groups = useMemo(() => groupByYearMonth(items), [items]);

    return (
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
            <Subheading>{t("news")}</Subheading>

            {items.length === 0 ? (
                <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                    <Text light>{t("no_news_found") || "Er is nog geen nieuws gepubliceerd"}</Text>
                </Box>
            ) : (
                groups.map((g) => (
                    <Box key={`${g.year}-${g.month}`} sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        <Box
                            sx={{
                                position: "sticky",
                                top: 0,
                                zIndex: 1,
                                background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                py: 1,
                                fontSize: 13,
                                fontWeight: 700,
                                color: colors.txt["secondary"],
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                            }}
                        >
                            {formatGroupHeader(g.year, g.month, i18n.language, t)}
                        </Box>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                                gap: 2,
                            }}
                        >
                            {g.items.map((a) => (
                                <ButtonBase
                                    key={a.id}
                                    onClick={() => setViewArticle(a)}
                                    sx={{
                                        borderRadius: 4,
                                        overflow: "hidden",
                                        background: theme.palette.mode === "dark" ? colors.grey[300] : "#fff",
                                        boxShadow: `0px 1px 2px 1px rgba(49, 65, 95, 0.15)`,
                                        transition: "150ms",
                                        display: "flex",
                                        flexDirection: "column",
                                        textAlign: "left",
                                        "&:hover": {
                                            boxShadow: `0px 4px 12px rgba(49, 65, 95, 0.2)`,
                                            transform: "translateY(-2px)",
                                        },
                                    }}
                                >
                                    {a.headerImage?.url ? (
                                        <Box
                                            component="img"
                                            src={a.headerImage.url}
                                            alt=""
                                            sx={{
                                                width: "100%",
                                                height: 140,
                                                objectFit: "cover",
                                                display: "block",
                                            }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                width: "100%",
                                                height: 140,
                                                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                            }}
                                        />
                                    )}
                                    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 0.5, width: 1 }}>
                                        <Text bold fontSize={15}>
                                            {a.title || t("untitled") || "Zonder titel"}
                                        </Text>
                                        <Text light fontSize={12}>
                                            {formatDate(a.publishedAt, i18n.language)}
                                        </Text>
                                    </Box>
                                </ButtonBase>
                            ))}
                        </Box>
                    </Box>
                ))
            )}

            <MViewNewsArticle
                open={Boolean(viewArticle)}
                handleClose={() => setViewArticle(null)}
                article={viewArticle}
            />
        </Box>
    );
};

export default News;
